import { useMemo } from "react";
import { create } from "zustand";
import { http } from "@/libs/http";
import { toast } from "sonner";
import type {
  Idp,
  IdpCreate,
  IdpHealthSummary,
  IdpType,
  IdpUpdate,
} from "@/types";

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unexpected error";
};

const normalizeIdp = (payload: Partial<Idp>): Idp => ({
  id: payload.id || "",
  display_name: payload.display_name || "Unnamed provider",
  logo_url: payload.logo_url,
  enabled: payload.enabled ?? payload.status === 1,
  provider: payload.provider || "okta",
  status: payload.status ?? (payload.enabled === false ? 2 : 1),
  tenant_id: payload.tenant_id,
  created_at: payload.created_at,
  updated_at: payload.updated_at,
});

interface IdpState {
  idps: Idp[];
  idpTypes: IdpType[];
  isLoading: boolean;
  error: string | null;

  // Core actions
  list: (filter?: (idps: Idp[]) => Idp[]) => Promise<Idp[]>;
  get: (id: string) => Promise<Idp>;
  create: (data: IdpCreate) => Promise<void>;
  update: (id: string, data: IdpUpdate) => Promise<void>;
  delete: (id: string) => Promise<void>;
  select: (criteria: (idps: Idp[]) => Idp) => Idp;

  // Additional actions
  listIdpTypes: () => Promise<IdpType[]>;
  listIdpTenants: (id: string) => Promise<Record<string, unknown>[]>;
  assignTenant: (id: string, tenantId: string) => Promise<void>;
  unassignTenant: (id: string, tenantId: string) => Promise<void>;
  getHealth: (id: string) => Promise<IdpHealthSummary>;
}

export const useIdps = create<IdpState>((set, get) => ({
  idps: [],
  idpTypes: [],
  isLoading: false,
  error: null,

  list: async (filter) => {
    set({ isLoading: true, error: null });
    try {
      const data = await http.get<Idp[] | PaginatedResponse<Idp>>(
        "/mgmt/v1/idps",
      );
      const rawItems = Array.isArray(data) ? data : data.items || [];
      const items = rawItems.map(normalizeIdp);
      set({ idps: items, isLoading: false });
      return filter ? filter(items) : items;
    } catch (err: unknown) {
      const errorMsg = toErrorMessage(err);
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
      return [];
    }
  },

  get: async (id) => {
    const found = get().idps.find((idp) => idp.id === id);
    if (found) return found;
    const data = normalizeIdp(await http.get<Idp>(`/mgmt/v1/idps/${id}`));
    set({ idps: [...get().idps.filter((i) => i.id !== id), data] });
    return data;
  },

  create: async (data) => {
    const previous = get().idps;
    const tempId = `temp-${Date.now()}`;
    const tempIdp = normalizeIdp({ ...data, id: tempId });
    set({ idps: [...previous, tempIdp] });
    try {
      const payload = {
        ...data,
        logo_url: data.logo_url ?? data.logo_data_url,
      };
      const result = normalizeIdp(
        await http.post<Idp>("/mgmt/v1/idps", payload),
      );
      set({ idps: get().idps.map((i) => (i.id === tempId ? result : i)) });
      toast.success("IdP created successfully");
    } catch (err: unknown) {
      const errorMsg = toErrorMessage(err);
      set({ idps: previous, error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  update: async (id, data) => {
    const previous = get().idps;
    set({
      idps: previous.map((i) =>
        i.id === id ? normalizeIdp({ ...i, ...data }) : i,
      ) as Idp[],
    });
    try {
      const payload = {
        ...data,
        logo_url: data.logo_url ?? data.logo_data_url,
      };
      const result = normalizeIdp(
        await http.put<Idp>(`/mgmt/v1/idps/${id}`, payload),
      );
      set({ idps: get().idps.map((i) => (i.id === id ? result : i)) });
      toast.success("IdP updated successfully");
    } catch (err: unknown) {
      const errorMsg = toErrorMessage(err);
      set({ idps: previous, error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  delete: async (id) => {
    const previous = get().idps;
    set({ idps: previous.filter((i) => i.id !== id) });
    try {
      await http.delete(`/mgmt/v1/idps/${id}`);
    } catch (err: unknown) {
      const errorMsg = toErrorMessage(err);
      set({ idps: previous, error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  select: (criteria) => {
    return criteria(get().idps);
  },

  listIdpTypes: async () => {
    const { items } = await http.get<{ items: IdpType[] }>(
      "/mgmt/v1/idps/types",
    );
    console.log(items);
    set({ idpTypes: items });
    return items;
  },

  listIdpTenants: async (id) => {
    const { items } = await http.get<{ items: Record<string, unknown>[] }>(
      `/mgmt/v1/idps/${id}/tenants`,
    );
    return items;
  },
  assignTenant: async (id, tenantId) =>
    http.post(`/mgmt/v1/idps/${id}/tenants`, { tenant_id: tenantId }),
  unassignTenant: async (id, tenantId) =>
    http.delete(`/mgmt/v1/idps/${id}/tenants/${tenantId}`),
  getHealth: async (id) =>
    http.get<IdpHealthSummary>(`/mgmt/v1/idps/${id}/health`),
}));

export const useIdp = (id?: string) => {
  const idp = useIdps((state) =>
    id ? (state.idps.find((item) => item.id === id) ?? null) : null,
  );

  return useMemo(() => {
    const ensureId = (): string => {
      if (!id) throw new Error("IdP id is required.");
      return id;
    };

    return {
      idp,
      get: () => useIdps.getState().get(ensureId()),
      update: (data: IdpUpdate) => useIdps.getState().update(ensureId(), data),
      delete: () => useIdps.getState().delete(ensureId()),
      tenants: {
        list: () => useIdps.getState().listIdpTenants(ensureId()),
        assign: (tenantId: string) =>
          useIdps.getState().assignTenant(ensureId(), tenantId),
        unassign: (tenantId: string) =>
          useIdps.getState().unassignTenant(ensureId(), tenantId),
      },
      health: {
        get: () => useIdps.getState().getHealth(ensureId()),
      },
      listTypes: () => useIdps.getState().listIdpTypes(),
    };
  }, [idp, id]);
};
